if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form || !this.variantIdInput) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // The theme editor can briefly retain both cart presenters after the
        // cart type setting changes. Prefer the drawer whenever it exists so
        // add-to-cart refreshes and opens the configured right-side panel.
        this.cart = this.resolveCartPresenter();
        this.submitButton = this.querySelector('[name="add"][type="submit"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('.main-atc__label__text, .button__label');
        this.isSubmitting = false;

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      handleSubmit(evt, options = {}) {
        this.onSubmitHandler(evt, options);
      }

      onSubmitHandler(evt, options = {}) {
        evt.preventDefault();
        // Sections can be replaced without reconstructing this product-form in
        // the theme editor. Resolve the live presenter for every submission.
        this.cart = this.resolveCartPresenter();
        const activeSubmitButton = options.submitButton || evt.submitter || this.submitButton;
        if (!activeSubmitButton || this.isSubmitting) return;
        if (activeSubmitButton.disabled || activeSubmitButton.getAttribute('aria-disabled') === 'true') return;

        // Product/cart upsells keep their selected variant on the upsell
        // element. They can be nested inside the main product-info element,
        // so validating them against the main product variant picker rejects
        // an otherwise valid add-to-cart request.
        const upsell = this.closest('product-info-upsell, cart-drawer-upsell');
        if (upsell?.dataset.id) this.variantIdInput.value = upsell.dataset.id;

        const variantPicker = upsell ? null : this.closest('product-info')?.querySelector('variant-selects');
        if (!upsell && this.closest('product-info')?.querySelector('subscription-widget')?.dataset.variantPending === 'true') {
          this.handleErrorMessage('Please wait for the selected purchase options to finish updating.');
          return;
        }
        const selectedVariantId = variantPicker?.dataset.selectedVariantId;
        const resolvedVariantId = variantPicker?.selectedVariant?.id;
        if (!this.variantIdInput.value) {
          this.variantIdInput.value = resolvedVariantId || selectedVariantId || this.dataset.variantId || '';
        }
        const submittedVariantId = this.variantIdInput.value;
        const selectedOptionsMatch =
          !resolvedVariantId || String(resolvedVariantId) === String(submittedVariantId);
        if (
          !submittedVariantId ||
          (selectedVariantId && submittedVariantId !== selectedVariantId) ||
          !selectedOptionsMatch
        ) {
          const message = 'The selected product option could not be confirmed. Please select the option again.';
          this.handleErrorMessage(message);
          this.dispatchCartErrorEvent(message, 'INVALID_VARIANT');
          console.error(message, { submittedVariantId, selectedVariantId });
          return;
        }

        const skipCart = options.skipCart === true
          || activeSubmitButton.dataset.skipCartButton === 'true'
          || this.variantIdInput.dataset.skipCart === 'true';

        this.handleErrorMessage();
        this.error = false;
        this.isSubmitting = true;

        activeSubmitButton.setAttribute('aria-disabled', 'true');
        activeSubmitButton.classList.add('loading');
        const loadingSpinner = activeSubmitButton.querySelector('.loading-overlay__spinner, .loading__spinner');
        loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // Subscription widgets sit outside the product form and Shopify/app
        // scripts may recreate or clear their hidden selling_plan input during
        // product-info updates. Restore a missing field from the live widget,
        // but never overwrite or delete a plan supplied by the submitted form.
        // One-time selection is handled by the widget disabling its own input
        // before FormData is created; absence of a widget is not that action.
        if (!upsell) {
          const subscriptionWidget = this.closest('product-info')?.querySelector('subscription-widget');
          const subscriptionSelected = subscriptionWidget
            ?.querySelector('.subscription__box--subscription')
            ?.dataset.selected === 'true';
          const selectedPlan = subscriptionWidget?.querySelector(
            'select option:checked, input[data-selling-plan-id]:checked'
          );
          const sellingPlanId = selectedPlan?.dataset.sellingPlanId || selectedPlan?.value;
          if (!formData.has('selling_plan') && subscriptionWidget?.dataset?.planManaged !== 'external' && subscriptionSelected && sellingPlanId) {
            formData.set('selling_plan', sellingPlanId);
          }
        }
        if (this.cart && !skipCart) {
          const sections = typeof this.cart.getSectionsToRender === 'function'
            ? this.cart.getSectionsToRender()
            : [
                { id: 'cart-drawer' },
                { id: 'cart-icon-bubble' },
              ];
          formData.append(
            'sections',
            sections.map((section) => section.section || section.id).filter(Boolean).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        if (!upsell) this.appendSelectedProductUpsells(formData);
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        let addConfirmed = false;
        const sendRequest = () => window.GeneralStoreRuntime?.requestJson
          ? window.GeneralStoreRuntime.requestJson(`${routes.cart_add_url}`, config).catch((error) => {
              // Keep the existing Shopify business-error UI/events. Network,
              // timeout and invalid responses must take the safe failure path.
              if (error.responseData && error.responseStatus >= 400 && error.responseStatus < 500) {
                return { ...error.responseData, status: error.responseStatus };
              }
              throw error;
            })
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        const drawer = document.querySelector('cart-drawer');
        const request = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(sendRequest) : sendRequest();
        return request
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = activeSubmitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeSubmitButton.setAttribute('aria-disabled', 'true');
              this.getButtonLabel(activeSubmitButton)?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!response.id && !response.items?.some((item) => item.id)) {
              throw new Error('Cart add response is missing line items');
            }
            addConfirmed = true;

            if (skipCart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              await document.querySelector('cart-drawer')?.prepareCheckout?.();
              const rootPath = window.Shopify?.routes?.root || '/';
              const checkoutUrl = `${rootPath}checkout`;
              if (window.Shopify?.designMode) {
                window.location.assign(window.routes?.cart_url || `${rootPath}cart`);
              } else {
                window.location.assign(checkoutUrl);
              }
              return;
            }

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              quickAddModal.hide(true);
              // Keep rendering inside this promise: an exception in a detached
              // modalClosed/timer callback must not escape the recovery path.
              this.measureCartRender(() => this.renderCartContents(response));
            } else {
              // Rendering the purchase result is the critical path. Open the
              // drawer before notifying optional subscribers such as quantity
              // rules, recommendations, analytics, or app integrations.
              this.measureCartRender(() => this.renderCartContents(response));
            }

            const startMarker = window.CartPerformance?.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              })).then(() => {
                if (startMarker) window.CartPerformance?.measureFromMarker('add:wait-for-subscribers', startMarker);
              }).catch((error) => console.error('Cart update subscribers failed', error));
          })
          .catch((e) => {
            console.error(e);
            if (addConfirmed) {
              if (skipCart) {
                this.handleErrorMessage(e.message || 'Unable to synchronize cart. Please view your cart and retry checkout.');
                this.showCartRecoveryLink();
                return;
              }
              // The cart has already changed. Go to its server-rendered page;
              // do not tell the shopper to add the same item again.
              window.location.assign(window.routes.cart_url);
              return;
            }
            const message = e.responseData
              ? e.userMessage
              : 'We could not confirm the cart update. Please check your cart before trying again.';
            this.handleErrorMessage(message);
            if (!e.responseData) this.showCartRecoveryLink();
            this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            activeSubmitButton.classList.remove('loading');
            if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
            loadingSpinner?.classList.add('hidden');

            window.CartPerformance?.measureFromEvent('add:user-action', evt);
          });
      }

      appendSelectedProductUpsells(formData) {
        if (Array.from(formData.keys()).some((key) => key.startsWith('items['))) return;
        const selected = Array.from(this.closest('product-info')?.querySelectorAll(
          'product-info-upsell[data-toggle="true"][data-selected="true"][data-id]'
        ) || []).filter((item) => item.dataset.variantAvailable !== 'false');
        if (!selected.length) return;
        for (const [key, value] of Array.from(formData.entries())) {
          if (['id', 'quantity', 'selling_plan'].includes(key) || key.startsWith('properties[')) {
            const itemKey = key.startsWith('properties[')
              ? 'items[0][properties]' + key.slice('properties'.length)
              : 'items[0][' + key + ']';
            formData.append(itemKey, value);
            formData.delete(key);
          }
        }
        selected.forEach((item, index) => {
          const prefix = 'items[' + (index + 1) + ']';
          formData.append(prefix + '[id]', item.dataset.id);
          formData.append(prefix + '[quantity]', '1');
          formData.append(prefix + '[properties][_theme_additional_key]', item.dataset.upsellKey || item.dataset.handle);
          const plan = item.querySelector('[name="selling_plan"]:not(:disabled)')?.value;
          if (plan) formData.append(prefix + '[selling_plan]', plan);
        });
      }

      getButtonLabel(button) {
        return button?.querySelector('.main-atc__label__text, .button__label');
      }

      resolveCartPresenter() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      renderCartContents(response) {
        const presenter = this.resolveCartPresenter();
        this.cart = presenter;
        if (!presenter) {
          window.location.assign(window.routes.cart_url);
          return;
        }

        if (typeof presenter.renderContents === 'function') {
          presenter.renderContents(response);
          return;
        }

        // Resilient drawer fallback for Shopify editor section reloads or a
        // custom-element registration failure. It mirrors CartDrawer without
        // requiring the element to have been upgraded first.
        if (presenter.tagName === 'CART-DRAWER') {
          const parser = new DOMParser();
          const drawerHtml = response.sections?.['cart-drawer'];
          const bubbleHtml = response.sections?.['cart-icon-bubble'];
          const source = drawerHtml && parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          const target = presenter.querySelector('#CartDrawer');
          const bubbleSource = bubbleHtml && parser.parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
          const bubbleTarget = document.querySelector('#cart-icon-bubble');
          if (!source || !target || !bubbleSource || !bubbleTarget) throw new Error('Cart sections could not be rendered');
          target.innerHTML = source.innerHTML;
          bubbleTarget.replaceWith(bubbleSource);
          presenter.classList.remove('is-empty');
          presenter.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          presenter.querySelector('.drawer__inner')?.focus();
          return;
        }

        window.location.assign(window.routes.cart_url);
      }

      measureCartRender(callback) {
        if (window.CartPerformance?.measure) {
          window.CartPerformance.measure('add:paint-updated-sections', callback);
        } else {
          callback();
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        // Product-card quick add has no error container in some layouts.
        // Errors must remain visible even there; hideErrors cannot swallow a
        // failed/uncertain transaction.
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const label = document.createElement('span');
          label.className = 'product-form__error-message';
          this.errorMessageWrapper.append(label);
          this.append(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessageWrapper.querySelector('[data-cart-recovery]')?.remove();
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      showCartRecoveryLink() {
        if (!this.errorMessageWrapper) return;
        const link = document.createElement('a');
        link.href = window.routes.cart_url;
        link.dataset.cartRecovery = 'true';
        link.textContent = ' View cart';
        this.errorMessageWrapper.append(link);
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        const purchaseButtons = [
          this.submitButton,
          ...this.querySelectorAll('[data-skip-cart-button="true"]'),
        ];
        if (disable) {
          purchaseButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          purchaseButtons.forEach((button) => {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          });
          if (this.submitButtonText) {
            this.submitButtonText.textContent = this.submitButton.dataset.customLabel || window.variantStrings.addToCart;
          }
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
